import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from '../components/layout/Sidebar';
import Header from '../components/layout/Header';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { 
  Building2, Users, Trash2, Plus, Edit2, RefreshCw, AlertTriangle,
  Loader2, Crown, UserCog, User, Database, Package, Grid3X3,
  CreditCard, Zap
} from 'lucide-react';
import { toast } from 'sonner';
import { useSafeMode } from '../contexts/SafeModeContext';
import { cn } from '../lib/utils';
import { adminTestingApi } from '../services/api';
import GridSelectionModal from '../components/admin/GridSelectionModal';

const AdminTestingPage = () => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  const [error, setError] = useState(null);
  
  // Data state
  const [organizations, setOrganizations] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedOrgFilter, setSelectedOrgFilter] = useState('all');
  
  // Modal state
  const [showOrgModal, setShowOrgModal] = useState(false);
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [showClearDataModal, setShowClearDataModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showGridModal, setShowGridModal] = useState(false);
  const [gridModalOrg, setGridModalOrg] = useState(null);
  
  // Form state
  const [orgForm, setOrgForm] = useState({ name: '', plan_tier: 'free' });
  const [memberForm, setMemberForm] = useState({ 
    email: '', name: '', password: '', role: 'owner', organization_id: '' 
  });
  const [clearDataForm, setClearDataForm] = useState({
    organization_id: '',
    clear_products: true,
    clear_features: true,
    clear_epics: true,
    clear_personas: true,
    clear_goals: true,
    clear_releases: true,
    clear_notifications: true,
    clear_chat_history: true
  });
  
  // Edit mode
  const [editingOrg, setEditingOrg] = useState(null);
  const [editingMember, setEditingMember] = useState(null);
  
  // Saving state
  const [saving, setSaving] = useState(false);
  
  // Plan management state
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [planChangeOrg, setPlanChangeOrg] = useState(null);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [changingPlan, setChangingPlan] = useState(false);

  // Fetch all data
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [orgsRes, membersRes, statsRes] = await Promise.all([
        adminTestingApi.listOrganizations(),
        adminTestingApi.listTeamMembers(),
        adminTestingApi.getStats()
      ]);
      setOrganizations(orgsRes.data.organizations || []);
      setTeamMembers(membersRes.data.team_members || []);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.response?.data?.detail || 'Failed to load testing data. Make sure you have admin access.');
      toast.error('Failed to load testing data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Organization handlers
  const handleSaveOrg = async () => {
    setSaving(true);
    try {
      if (editingOrg) {
        await adminTestingApi.updateOrganization(editingOrg.organization_id, orgForm);
        toast.success('Organization updated');
      } else {
        await adminTestingApi.createOrganization(orgForm);
        toast.success('Organization created');
      }
      setShowOrgModal(false);
      setOrgForm({ name: '', plan_tier: 'free' });
      setEditingOrg(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save organization');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteOrg = async (orgId) => {
    setSaving(true);
    try {
      const result = await adminTestingApi.deleteOrganization(orgId);
      toast.success(`Organization deleted. Removed: ${Object.entries(result.data.deleted).map(([k, v]) => `${v} ${k}`).join(', ')}`);
      setShowDeleteConfirm(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete organization');
    } finally {
      setSaving(false);
    }
  };

  // Team member handlers
  const handleSaveMember = async () => {
    setSaving(true);
    try {
      if (editingMember) {
        await adminTestingApi.updateTeamMember(editingMember.member_id, {
          name: memberForm.name,
          role: memberForm.role,
          organization_id: memberForm.organization_id
        });
        toast.success('Team member updated');
      } else {
        await adminTestingApi.createTeamMember(memberForm);
        toast.success('Team member created');
      }
      setShowMemberModal(false);
      setMemberForm({ email: '', name: '', password: '', role: 'owner', organization_id: '' });
      setEditingMember(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save team member');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMember = async (memberId) => {
    setSaving(true);
    try {
      await adminTestingApi.deleteTeamMember(memberId);
      toast.success('Team member deleted');
      setShowDeleteConfirm(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete team member');
    } finally {
      setSaving(false);
    }
  };

  // Plan management handlers
  const handleOpenPlanModal = async (org) => {
    setPlanChangeOrg(org);
    setAvailablePlans([]);
    setSelectedPlan(null);
    setShowPlanModal(true);
    setLoadingPlans(true);
    
    try {
      const response = await adminTestingApi.getPricingGrid(org.organization_id);
      const plans = response.data.plans || [];
      setAvailablePlans(plans);
      
      // Find and set the current plan as selected
      const currentPlanId = response.data.current_plan_id;
      const currentPlan = plans.find(p => p.plan_id === currentPlanId) || 
                          plans.find(p => p.tier === org.plan_tier) ||
                          plans[0];
      setSelectedPlan(currentPlan);
    } catch (error) {
      console.error('Failed to load pricing grid:', error);
      toast.error('Failed to load available plans');
      // Set default plans as fallback
      setAvailablePlans([
        { plan_id: 'free', tier: 'free', name: 'Free', price: 0, max_products: 3 },
        { plan_id: 'pro', tier: 'paid', name: 'Pro', price: 29, max_products: 999 }
      ]);
    } finally {
      setLoadingPlans(false);
    }
  };

  const handleChangePlan = async () => {
    if (!planChangeOrg || !selectedPlan) return;
    
    // Don't change if same plan is selected
    if (selectedPlan.plan_id === planChangeOrg.selected_plan_id || 
        (selectedPlan.tier === planChangeOrg.plan_tier && !planChangeOrg.selected_plan_id)) {
      toast.info('This is already the current plan');
      return;
    }
    
    setChangingPlan(true);
    try {
      const result = await adminTestingApi.changePlan(planChangeOrg.organization_id, {
        plan_id: selectedPlan.plan_id,
        plan_tier: selectedPlan.tier
      });
      
      const isDowngrade = selectedPlan.tier === 'free' && planChangeOrg.plan_tier !== 'free';
      const isUpgrade = selectedPlan.tier !== 'free' && planChangeOrg.plan_tier === 'free';
      
      let message = `Plan changed to ${selectedPlan.name}`;
      if (result.data?.archived_products > 0) {
        message += `. ${result.data.archived_products} product(s) archived`;
      }
      if (result.data?.demoted_members > 0) {
        message += `. ${result.data.demoted_members} member(s) demoted to Collaborator`;
      }
      
      toast.success(message, { duration: 5000 });
      
      setShowPlanModal(false);
      setPlanChangeOrg(null);
      setAvailablePlans([]);
      setSelectedPlan(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change plan');
    } finally {
      setChangingPlan(false);
    }
  };

  // Clear data handler
  const handleClearData = async () => {
    setSaving(true);
    try {
      const result = await adminTestingApi.clearData(clearDataForm);
      const deletedSummary = Object.entries(result.data.deleted)
        .filter(([_, count]) => count > 0)
        .map(([type, count]) => `${count} ${type}`)
        .join(', ');
      toast.success(`Data cleared: ${deletedSummary || 'No data to clear'}`);
      setShowClearDataModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clear data');
    } finally {
      setSaving(false);
    }
  };

  const openEditOrg = (org) => {
    setEditingOrg(org);
    setOrgForm({ name: org.name, plan_tier: org.plan_tier });
    setShowOrgModal(true);
  };

  const openGridModal = (org) => {
    setGridModalOrg(org);
    setShowGridModal(true);
  };

  const openEditMember = (member) => {
    setEditingMember(member);
    setMemberForm({
      email: member.email,
      name: member.name,
      password: '',
      role: member.role,
      organization_id: member.organization_id
    });
    setShowMemberModal(true);
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'owner': return <Crown className="w-4 h-4 text-yellow-500" />;
      case 'product_manager': return <UserCog className="w-4 h-4 text-blue-500" />;
      default: return <User className="w-4 h-4 text-gray-500" />;
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'owner': return 'Owner';
      case 'product_manager': return 'Product Manager';
      case 'collaborator': return 'Collaborator';
      default: return role;
    }
  };

  const filteredMembers = selectedOrgFilter === 'all' 
    ? teamMembers 
    : teamMembers.filter(m => m.organization_id === selectedOrgFilter);

  return (
    <div className={cn("flex h-screen", colors.bgPrimary)}>
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header title="Manage Testing" />
        
        <main className="flex-1 overflow-y-auto p-8">
          <div className="max-w-6xl mx-auto">
            {/* Error State */}
            {error && (
              <Card className={cn("p-6 mb-6 border-red-200 bg-red-50", colors.borderPrimary)}>
                <div className="flex items-center gap-3 text-red-600">
                  <AlertTriangle className="w-5 h-5" />
                  <div>
                    <p className="font-medium">Access Denied</p>
                    <p className="text-sm">{error}</p>
                  </div>
                </div>
              </Card>
            )}
            
            {/* Stats Overview */}
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
                <Card className={cn("p-4 border", colors.bgCard, colors.borderPrimary)}>
                  <div className={cn("flex items-center gap-2 text-sm mb-1", colors.textMuted)}>
                    <Building2 className="w-4 h-4" />
                    Organizations
                  </div>
                  <div className={cn("text-2xl font-bold", colors.textPrimary)}>{stats.organizations}</div>
                </Card>
                <Card className={cn("p-4 border", colors.bgCard, colors.borderPrimary)}>
                  <div className={cn("flex items-center gap-2 text-sm mb-1", colors.textMuted)}>
                    <Users className="w-4 h-4" />
                    Users
                  </div>
                  <div className={cn("text-2xl font-bold", colors.textPrimary)}>{stats.users}</div>
                </Card>
                <Card className={cn("p-4 border", colors.bgCard, colors.borderPrimary)}>
                  <div className={cn("flex items-center gap-2 text-sm mb-1", colors.textMuted)}>
                    <Package className="w-4 h-4" />
                    Products
                  </div>
                  <div className={cn("text-2xl font-bold", colors.textPrimary)}>{stats.products}</div>
                </Card>
                <Card className={cn("p-4 border", colors.bgCard, colors.borderPrimary)}>
                  <div className={cn("flex items-center gap-2 text-sm mb-1", colors.textMuted)}>
                    <Database className="w-4 h-4" />
                    Epics
                  </div>
                  <div className={cn("text-2xl font-bold", colors.textPrimary)}>{stats.epics}</div>
                </Card>
                <Card className={cn("p-4 border", colors.bgCard, colors.borderPrimary)}>
                  <div className={cn("flex items-center gap-2 text-sm mb-1", colors.textMuted)}>
                    <Database className="w-4 h-4" />
                    Features
                  </div>
                  <div className={cn("text-2xl font-bold", colors.textPrimary)}>{stats.features}</div>
                </Card>
                <Card className={cn("p-4 border", colors.bgCard, colors.borderPrimary)}>
                  <div className={cn("flex items-center gap-2 text-sm mb-1", colors.textMuted)}>
                    <Users className="w-4 h-4" />
                    Personas
                  </div>
                  <div className={cn("text-2xl font-bold", colors.textPrimary)}>{stats.personas}</div>
                </Card>
              </div>
            )}

            <Tabs defaultValue="organizations" className="space-y-6">
              <div className="flex items-center justify-between">
                <TabsList className={cn("border p-1 h-auto gap-1", colors.bgSecondary, colors.borderPrimary)}>
                  <TabsTrigger value="organizations" className={cn("data-[state=active]:shadow-sm transition-all", colors.tabActive)}>Organizations</TabsTrigger>
                  <TabsTrigger value="team-members" className={cn("data-[state=active]:shadow-sm transition-all", colors.tabActive)}>Team Members</TabsTrigger>
                  <TabsTrigger value="data-management" className={cn("data-[state=active]:shadow-sm transition-all", colors.tabActive)}>Data Management</TabsTrigger>
                </TabsList>
                
                <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
                  <RefreshCw className={cn("w-4 h-4 mr-2", loading && "animate-spin")} />
                  Refresh
                </Button>
              </div>

              {/* Organizations Tab */}
              <TabsContent value="organizations">
                <Card className={cn("p-6 border", colors.bgCard, colors.borderPrimary)}>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className={cn("text-lg font-semibold", colors.textPrimary)}>Test Organizations</h2>
                    <Button 
                      onClick={() => { setEditingOrg(null); setOrgForm({ name: '', plan_tier: 'free' }); setShowOrgModal(true); }}
                      className={cn(colors.accentGradient, colors.accentGradientHover)}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      New Organization
                    </Button>
                  </div>

                  {loading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className={cn("w-8 h-8 animate-spin", colors.textMuted)} />
                    </div>
                  ) : organizations.length === 0 ? (
                    <p className={cn("text-center py-8", colors.textMuted)}>No test organizations found</p>
                  ) : (
                    <div className="space-y-3">
                      {organizations.map(org => (
                        <div key={org.organization_id} className={cn("flex items-center justify-between p-4 rounded-lg border", colors.bgSecondary, colors.borderPrimary)}>
                          <div className="flex items-center gap-4">
                            <Building2 className={cn("w-5 h-5", colors.textMuted)} />
                            <div>
                              <div className={cn("font-medium", colors.textPrimary)}>{org.name}</div>
                              <div className={cn("text-sm", colors.textMuted)}>
                                {org.member_count} members • {org.product_count} products
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={cn(
                                "px-2 py-1 text-xs font-medium rounded",
                                org.plan_tier === 'paid' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                              )}>
                                {org.plan_tier === 'paid' ? 'Paid' : 'Free'}
                              </span>
                              {org.grid_version_id && (
                                <Badge variant="outline" className="text-xs">
                                  Grid: {org.grid_version_id?.slice(-6)}
                                </Badge>
                              )}
                              {org.downgrade_grace_period_ends && (
                                <Badge variant="destructive" className="text-xs">
                                  Grace Period
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => openGridModal(org)}
                              title="Change Grid/Plan"
                            >
                              <Grid3X3 className="w-4 h-4 mr-1" />
                              Grid
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleOpenPlanModal(org)}
                              title="Change Plan"
                              className="text-blue-600 hover:bg-blue-50"
                            >
                              <CreditCard className="w-4 h-4 mr-1" />
                              Change Plan
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => openEditOrg(org)}>
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => setShowDeleteConfirm({ type: 'org', item: org })}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              </TabsContent>

              {/* Team Members Tab */}
              <TabsContent value="team-members">
                <Card className={cn("p-6 border", colors.bgCard, colors.borderPrimary)}>
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-4">
                      <h2 className={cn("text-lg font-semibold", colors.textPrimary)}>Test Team Members</h2>
                      <Select value={selectedOrgFilter} onValueChange={setSelectedOrgFilter}>
                        <SelectTrigger className="w-48">
                          <SelectValue placeholder="Filter by org" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Organizations</SelectItem>
                          {organizations.map(org => (
                            <SelectItem key={org.organization_id} value={org.organization_id}>
                              {org.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button 
                      onClick={() => { 
                        setEditingMember(null); 
                        setMemberForm({ email: '', name: '', password: '', role: 'owner', organization_id: organizations[0]?.organization_id || '' }); 
                        setShowMemberModal(true); 
                      }}
                      disabled={organizations.length === 0}
                      className={cn(colors.accentGradient, colors.accentGradientHover)}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      New Team Member
                    </Button>
                  </div>

                  {loading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className={cn("w-8 h-8 animate-spin", colors.textMuted)} />
                    </div>
                  ) : filteredMembers.length === 0 ? (
                    <p className={cn("text-center py-8", colors.textMuted)}>No team members found</p>
                  ) : (
                    <div className="space-y-3">
                      {filteredMembers.map(member => (
                        <div key={member.member_id} className={cn("flex items-center justify-between p-4 rounded-lg border", colors.bgSecondary, colors.borderPrimary)}>
                          <div className="flex items-center gap-4">
                            {getRoleIcon(member.role)}
                            <div>
                              <div className={cn("font-medium", colors.textPrimary)}>{member.name}</div>
                              <div className={cn("text-sm", colors.textMuted)}>{member.email}</div>
                            </div>
                            <span className="px-2 py-1 text-xs font-medium rounded bg-blue-100 text-blue-700">
                              {getRoleLabel(member.role)}
                            </span>
                            <span className={cn("text-sm", colors.textMuted)}>
                              {member.organization_name}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button variant="ghost" size="sm" onClick={() => openEditMember(member)}>
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => setShowDeleteConfirm({ type: 'member', item: member })}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              </TabsContent>

              {/* Data Management Tab */}
              <TabsContent value="data-management">
                <Card className={cn("p-6 border", colors.bgCard, colors.borderPrimary)}>
                  <div className="flex items-center gap-3 mb-6">
                    <AlertTriangle className="w-6 h-6 text-amber-500" />
                    <div>
                      <h2 className={cn("text-lg font-semibold", colors.textPrimary)}>Clear Test Data</h2>
                      <p className={cn("text-sm", colors.textMuted)}>
                        Remove products, features, and other data while keeping organizations and user accounts intact.
                      </p>
                    </div>
                  </div>

                <div className="space-y-4 mb-6">
                  <div>
                    <Label>Target Organization</Label>
                    <Select 
                      value={clearDataForm.organization_id || 'all'} 
                      onValueChange={(v) => setClearDataForm(prev => ({ ...prev, organization_id: v === 'all' ? '' : v }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select organization" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Test Organizations</SelectItem>
                        {organizations.map(org => (
                          <SelectItem key={org.organization_id} value={org.organization_id}>
                            {org.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                      { key: 'clear_products', label: 'Products' },
                      { key: 'clear_epics', label: 'Epics' },
                      { key: 'clear_features', label: 'Features' },
                      { key: 'clear_personas', label: 'Personas' },
                      { key: 'clear_goals', label: 'Goals & Initiatives' },
                      { key: 'clear_releases', label: 'Releases' },
                      { key: 'clear_notifications', label: 'Notifications' },
                      { key: 'clear_chat_history', label: 'Chat History' },
                    ].map(({ key, label }) => (
                      <div key={key} className="flex items-center gap-2">
                        <Checkbox 
                          id={key}
                          checked={clearDataForm[key]}
                          onCheckedChange={(checked) => setClearDataForm(prev => ({ ...prev, [key]: checked }))}
                        />
                        <Label htmlFor={key} className="text-sm cursor-pointer">{label}</Label>
                      </div>
                    ))}
                  </div>
                </div>

                <Button 
                  variant="destructive" 
                  onClick={() => setShowClearDataModal(true)}
                  disabled={loading}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Clear Selected Data
                </Button>
              </Card>
            </TabsContent>
            </Tabs>

            {/* Organization Modal */}
            <Dialog open={showOrgModal} onOpenChange={setShowOrgModal}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingOrg ? 'Edit Organization' : 'Create Organization'}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div>
                    <Label>Organization Name</Label>
                    <Input 
                      value={orgForm.name}
                      onChange={(e) => setOrgForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., Test Company Inc."
                    />
                  </div>
                  <div>
                    <Label>Plan Tier</Label>
                    <Select value={orgForm.plan_tier} onValueChange={(v) => setOrgForm(prev => ({ ...prev, plan_tier: v }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="free">Free</SelectItem>
                        <SelectItem value="paid">Paid</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowOrgModal(false)}>Cancel</Button>
                  <Button onClick={handleSaveOrg} disabled={saving || !orgForm.name} className={cn(colors.accentGradient, colors.accentGradientHover)}>
                    {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    {editingOrg ? 'Update' : 'Create'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Team Member Modal */}
            <Dialog open={showMemberModal} onOpenChange={setShowMemberModal}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingMember ? 'Edit Team Member' : 'Create Team Member'}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div>
                    <Label>Email</Label>
                    <Input 
                      type="email"
                      value={memberForm.email}
                      onChange={(e) => setMemberForm(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="user@example.com"
                      disabled={!!editingMember}
                    />
                  </div>
                  <div>
                    <Label>Name</Label>
                    <Input 
                      value={memberForm.name}
                      onChange={(e) => setMemberForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="John Doe"
                    />
                  </div>
                  {!editingMember && (
                    <div>
                      <Label>Password</Label>
                      <Input 
                        type="password"
                        value={memberForm.password}
                        onChange={(e) => setMemberForm(prev => ({ ...prev, password: e.target.value }))}
                        placeholder="••••••••"
                      />
                    </div>
                  )}
                  <div>
                    <Label>Role</Label>
                    <Select value={memberForm.role} onValueChange={(v) => setMemberForm(prev => ({ ...prev, role: v }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="owner">Owner</SelectItem>
                        <SelectItem value="product_manager">Product Manager</SelectItem>
                        <SelectItem value="collaborator">Collaborator</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Organization</Label>
                    <Select 
                      value={memberForm.organization_id} 
                      onValueChange={(v) => setMemberForm(prev => ({ ...prev, organization_id: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select organization" />
                      </SelectTrigger>
                      <SelectContent>
                        {organizations.map(org => (
                          <SelectItem key={org.organization_id} value={org.organization_id}>
                            {org.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowMemberModal(false)}>Cancel</Button>
                  <Button 
                    onClick={handleSaveMember} 
                    disabled={saving || !memberForm.name || !memberForm.organization_id || (!editingMember && (!memberForm.email || !memberForm.password))}
                    className={cn(colors.accentGradient, colors.accentGradientHover)}
                  >
                    {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    {editingMember ? 'Update' : 'Create'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Delete Confirmation Modal */}
            <Dialog open={!!showDeleteConfirm} onOpenChange={() => setShowDeleteConfirm(null)}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Confirm Delete</DialogTitle>
                  <DialogDescription>
                    {showDeleteConfirm?.type === 'org' 
                      ? `Are you sure you want to delete "${showDeleteConfirm.item.name}"? This will remove all associated users, team members, products, and data.`
                      : `Are you sure you want to delete ${showDeleteConfirm?.item?.name}? This will remove their user account and all associated data.`
                    }
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowDeleteConfirm(null)}>Cancel</Button>
                  <Button 
                    variant="destructive"
                    onClick={() => {
                      if (showDeleteConfirm.type === 'org') {
                        handleDeleteOrg(showDeleteConfirm.item.organization_id);
                      } else {
                        handleDeleteMember(showDeleteConfirm.item.member_id);
                      }
                    }}
                    disabled={saving}
                  >
                    {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Delete
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Clear Data Confirmation Modal */}
            <Dialog open={showClearDataModal} onOpenChange={setShowClearDataModal}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Confirm Clear Data</DialogTitle>
                  <DialogDescription>
                    This will permanently delete the selected data types from {clearDataForm.organization_id ? 'the selected organization' : 'all test organizations'}.
                    Organizations and user accounts will be preserved.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowClearDataModal(false)}>Cancel</Button>
                  <Button variant="destructive" onClick={handleClearData} disabled={saving}>
                    {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Clear Data
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Plan Change Modal */}
            <Dialog open={showPlanModal} onOpenChange={(open) => {
              setShowPlanModal(open);
              if (!open) {
                setPlanChangeOrg(null);
                setAvailablePlans([]);
                setSelectedPlan(null);
              }
            }}>
              <DialogContent className={cn(colors.bgCard)}>
                <DialogHeader>
                  <DialogTitle className={cn("flex items-center gap-2", colors.textPrimary)}>
                    <CreditCard className="w-5 h-5 text-blue-500" />
                    Change Plan
                  </DialogTitle>
                  <DialogDescription>
                    {planChangeOrg && (
                      <span>Select a new plan for <strong>{planChangeOrg.name}</strong></span>
                    )}
                  </DialogDescription>
                </DialogHeader>
                
                <div className="py-4 space-y-4">
                  {loadingPlans ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className={cn("w-6 h-6 animate-spin", colors.textMuted)} />
                    </div>
                  ) : (
                    <>
                      {/* Plan Selector Dropdown */}
                      <div>
                        <Label className={cn("mb-2 block", colors.textSecondary)}>Select Plan</Label>
                        <Select 
                          value={selectedPlan?.plan_id || ''} 
                          onValueChange={(planId) => {
                            const plan = availablePlans.find(p => p.plan_id === planId);
                            setSelectedPlan(plan);
                          }}
                        >
                          <SelectTrigger className={cn(colors.bgSecondary, colors.borderPrimary)}>
                            <SelectValue placeholder="Select a plan" />
                          </SelectTrigger>
                          <SelectContent>
                            {availablePlans.map(plan => (
                              <SelectItem key={plan.plan_id} value={plan.plan_id}>
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">{plan.name}</span>
                                  {plan.price !== undefined && (
                                    <span className="text-muted-foreground text-sm">
                                      {plan.price === 0 ? '(Free)' : `($${plan.price}/mo)`}
                                    </span>
                                  )}
                                  {(plan.plan_id === planChangeOrg?.selected_plan_id || 
                                    (!planChangeOrg?.selected_plan_id && plan.tier === planChangeOrg?.plan_tier)) && (
                                    <Badge variant="outline" className="ml-2 text-xs">Current</Badge>
                                  )}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Plan Details Card */}
                      {selectedPlan && (
                        <div className={cn("p-4 rounded-lg border", colors.bgSecondary, colors.borderPrimary)}>
                          <div className="flex items-center justify-between mb-3">
                            <h4 className={cn("font-semibold", colors.textPrimary)}>{selectedPlan.name}</h4>
                            <Badge className={selectedPlan.tier === 'paid' || selectedPlan.tier === 'pro' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}>
                              {selectedPlan.tier === 'paid' || selectedPlan.tier === 'pro' ? 'Paid' : 'Free'}
                            </Badge>
                          </div>
                          <div className="space-y-2 text-sm">
                            {selectedPlan.max_products && (
                              <div className="flex justify-between">
                                <span className={colors.textMuted}>Max Products:</span>
                                <span className={colors.textPrimary}>{selectedPlan.max_products === 999 ? 'Unlimited' : selectedPlan.max_products}</span>
                              </div>
                            )}
                            {selectedPlan.max_features && (
                              <div className="flex justify-between">
                                <span className={colors.textMuted}>Max Features:</span>
                                <span className={colors.textPrimary}>{selectedPlan.max_features === 999 ? 'Unlimited' : selectedPlan.max_features}</span>
                              </div>
                            )}
                            {selectedPlan.description && (
                              <p className={cn("mt-2 pt-2 border-t", colors.textMuted, colors.borderPrimary)}>
                                {selectedPlan.description}
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Downgrade Warning */}
                      {selectedPlan && selectedPlan.tier === 'free' && planChangeOrg?.plan_tier !== 'free' && (
                        <div className="p-4 rounded-lg border border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-900/20">
                          <div className="flex items-start gap-3">
                            <AlertTriangle className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="font-medium text-orange-800 dark:text-orange-300">Downgrade Warning</p>
                              <ul className="text-sm text-orange-700 dark:text-orange-400 mt-1 space-y-1">
                                <li>• Products beyond the free tier limit will be archived</li>
                                <li>• Team members (except owner/billing) will be demoted to Collaborator</li>
                                <li>• Users can restore products after upgrading again</li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Upgrade Info */}
                      {selectedPlan && (selectedPlan.tier === 'paid' || selectedPlan.tier === 'pro') && planChangeOrg?.plan_tier === 'free' && (
                        <div className="p-4 rounded-lg border border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20">
                          <div className="flex items-start gap-3">
                            <Zap className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="font-medium text-green-800 dark:text-green-300">Upgrade Benefits</p>
                              <ul className="text-sm text-green-700 dark:text-green-400 mt-1 space-y-1">
                                <li>• Unlimited products and features</li>
                                <li>• Advanced AI capabilities</li>
                                <li>• Archived products can be restored</li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Current vs New Plan Comparison */}
                      <div className={cn("p-3 rounded-lg", colors.bgTertiary)}>
                        <div className="flex items-center justify-between">
                          <span className={cn("text-sm", colors.textSecondary)}>Current Plan:</span>
                          <Badge className={planChangeOrg?.plan_tier === 'paid' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}>
                            {planChangeOrg?.plan_tier === 'paid' ? 'Pro' : 'Free'}
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between mt-2">
                          <span className={cn("text-sm", colors.textSecondary)}>New Plan:</span>
                          <Badge className={selectedPlan?.tier === 'paid' || selectedPlan?.tier === 'pro' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}>
                            {selectedPlan?.name || 'Select a plan'}
                          </Badge>
                        </div>
                      </div>
                    </>
                  )}
                </div>
                
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowPlanModal(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleChangePlan}
                    disabled={changingPlan || loadingPlans || !selectedPlan}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {changingPlan && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Change Plan
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Grid Selection Modal */}
            <GridSelectionModal
              isOpen={showGridModal}
              onClose={() => {
                setShowGridModal(false);
                setGridModalOrg(null);
              }}
              organization={gridModalOrg}
              onPlanChanged={fetchData}
              colors={colors}
            />
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminTestingPage;
